import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  BrainCircuit, Send, Loader2, Lock, GitBranch, Sigma, LineChart, CircleDot, PencilRuler,
  Plus, MessageSquare, ImagePlus, RotateCcw, AlertTriangle, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Whiteboard } from "@/components/Whiteboard";
import { UpgradeGate } from "@/components/UpgradeGate";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import { toast } from "sonner";

const SUBJECTS = [
  { id: "Economics", icon: LineChart, hint: "Supply/demand elasticity · IS-LM · Game theory" },
  { id: "Math", icon: Sigma, hint: "Proof loops · Multivariable calculus · Logic" },
];

const STARTERS = {
  Economics: [
    "Why does a perfectly inelastic demand curve look vertical?",
    "Walk me through how a tax shifts the supply curve.",
    "Explain the intuition behind Nash equilibrium.",
  ],
  Math: [
    "Help me prove the sum of the first n integers is n(n+1)/2.",
    "How do I set up a Lagrange multiplier problem?",
    "Why does the chain rule work?",
  ],
};

const CHAT_TIMEOUT_MS = 90000;
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function SocraticTutor({ onAuth, onNavigate }) {
  const { user } = useAuth();
  const [subject, setSubject] = useState("Economics");
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pane, setPane] = useState("logic");
  const [failed, setFailed] = useState(null); // { message, image }
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const newSessionId = useCallback(() => `${user.id}-${subject}-${uid()}`, [user, subject]);

  const loadSessions = useCallback(() => {
    api.get("/tutor/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  // Start a fresh session the first time the tutor opens.
  useEffect(() => {
    if (user && !sessionId) {
      setSessionId(newSessionId());
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const startNewChat = () => {
    setSessionId(newSessionId());
    setMessages([]);
    setFailed(null);
  };

  const openSession = async (s) => {
    setSessionId(s.session_id);
    setSubject(s.subject);
    setFailed(null);
    try {
      const { data } = await api.get(`/tutor/history/${s.session_id}`);
      setMessages(data);
    } catch {
      setMessages([]);
    }
  };

  const streamReply = async (payloadMsg, image) => {
    setFailed(null);
    setMessages((m) => [
      ...m,
      { id: uid(), role: "user", content: payloadMsg, image },
      { id: uid(), role: "assistant", content: "" },
    ]);
    setStreaming(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_BASE}/tutor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({ session_id: sessionId, subject, message: payloadMsg, image_base64: image || null }),
      });
      if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotError = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                const lastIdx = copy.length - 1;
                copy[lastIdx] = { ...copy[lastIdx], content: copy[lastIdx].content + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              gotError = true;
            } else if (obj.title) {
              loadSessions();
            }
          } catch { /* ignore malformed SSE line */ }
        }
      }
      if (gotError) throw new Error("tutor-unavailable");
      loadSessions();
    } catch (err) {
      console.error("Tutor stream failed", err);
      const aborted = err.name === "AbortError";
      setMessages((m) => m.filter((_, i) => i !== m.length - 1)); // drop empty assistant bubble
      setFailed({ message: payloadMsg, image });
      toast.error(aborted
        ? "That took too long to compute. Tap retry to try again."
        : "The tutor hit a snag. Tap retry to resend your message.");
    } finally {
      clearTimeout(timer);
      setStreaming(false);
    }
  };

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    streamReply(msg, null);
  };

  const retry = () => {
    if (!failed || streaming) return;
    const { message, image } = failed;
    streamReply(message, image);
  };

  const sendImage = (dataUrl, caption) => {
    if (streaming) return;
    streamReply(caption || "Here is my handwritten work — please review it and guide me.", dataUrl);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) return toast.error("Upload a PNG, JPEG, or WEBP image.");
    const reader = new FileReader();
    reader.onload = () => sendImage(reader.result, "Here is my work (photo) — please review and guide me.");
    reader.readAsDataURL(file);
  };

  const assistantTurns = messages.filter((m) => m.role === "assistant" && m.content).length;
  const attachSketch = (dataUrl) => sendImage(dataUrl, "Here is my whiteboard sketch — please review and guide me.");

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="h-14 w-14 rounded-xl bg-navy-soft flex items-center justify-center mx-auto mb-6">
          <Lock className="h-6 w-6 text-navy" />
        </div>
        <h2 className="font-serif text-3xl font-semibold text-navy">Sign in to consult the Socratic Tutor</h2>
        <p className="text-slate-600 mt-3">Your dialogues and reasoning history stay tied to your account.</p>
        <Button onClick={() => onAuth("login")} data-testid="socratic-auth-cta"
          className="mt-6 bg-navy hover:bg-navy-light text-white">Sign in / Enrol</Button>
      </div>
    );
  }

  if (!user.premium && user.role !== "admin") {
    return <UpgradeGate feature="the Socratic AI Tutor" onUpgrade={() => onNavigate("home")} />;
  }

  const ChatPane = (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm flex flex-col overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 bg-navy-soft/50">
        <BrainCircuit className="h-4 w-4 text-navy" />
        <span className="text-sm font-medium text-navy">Socratic dialogue · {subject}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="socratic-messages">
        {messages.length === 0 && !streaming && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <BrainCircuit className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm max-w-xs">Pose a problem you're stuck on. The tutor answers with a guiding question, not the solution.</p>
            <div className="mt-5 flex flex-col gap-2 w-full max-w-sm">
              {STARTERS[subject].map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`socratic-starter-${i}`}
                  className="text-left text-sm text-navy bg-navy-soft hover:bg-persian-blue/10 hover:ring-1 hover:ring-persian-blue rounded-lg px-3 py-2 transition-all">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          const isLastAssistant = !isUser && i === messages.length - 1;
          return (
            <div key={m.id || i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
              <div data-testid={`msg-${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                  isUser
                    ? "bg-navy text-white rounded-2xl rounded-br-md"
                    : "bg-white text-navy rounded-2xl rounded-bl-md border border-slate-200"
                }`}>
                {m.image && (
                  <img src={m.image} alt="student work" data-testid={`msg-image-${i}`}
                    className="rounded-lg border border-white/20 max-w-full mb-2" />
                )}
                {isUser ? (
                  <span className="whitespace-pre-wrap">{m.content}</span>
                ) : m.content ? (
                  <MarkdownMessage>{m.content}</MarkdownMessage>
                ) : streaming && isLastAssistant ? (
                  <span className="flex items-center gap-1 text-persian-blue">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs text-slate-400">thinking…</span>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
        {failed && !streaming && (
          <div className="flex justify-start" data-testid="chat-retry-banner">
            <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-persian-rose/30 bg-persian-rose/[0.06] px-4 py-3">
              <div className="flex items-center gap-2 text-persian-rose text-sm font-medium">
                <AlertTriangle className="h-4 w-4" /> Message didn't go through
              </div>
              <Button size="sm" onClick={retry} data-testid="chat-retry-button"
                className="mt-2 h-8 bg-navy hover:bg-navy-light text-white">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-slate-100">
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
            onChange={onPickFile} data-testid="socratic-image-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={streaming}
            data-testid="socratic-upload-button" className="h-11 w-11 p-0 shrink-0" title="Upload a photo of your work">
            <ImagePlus className="h-4 w-4" />
          </Button>
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Ask about ${subject.toLowerCase()}…`} data-testid="socratic-input-textarea"
            className="resize-none min-h-[44px] max-h-32" />
          <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="socratic-send-button"
            className="bg-persian-rose hover:bg-persian-rosehover text-white h-11 w-11 p-0 shrink-0">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );

  const RightPane = (
    <div className="bg-navy rounded-2xl p-6 text-white flex flex-col h-full">
      <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5 self-start mb-5">
        <button onClick={() => setPane("logic")} data-testid="pane-logic-tab"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            pane === "logic" ? "bg-persian-blue text-navy" : "text-slate-300 hover:text-white"}`}>
          <GitBranch className="h-4 w-4" /> Logic Tree
        </button>
        <button onClick={() => setPane("whiteboard")} data-testid="pane-whiteboard-tab"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            pane === "whiteboard" ? "bg-persian-blue text-navy" : "text-slate-300 hover:text-white"}`}>
          <PencilRuler className="h-4 w-4" /> Whiteboard
        </button>
      </div>
      {pane === "logic" ? (
        <div className="flex-1 overflow-y-auto">
          <p className="text-slate-400 text-sm">Each exchange advances one node in your reasoning loop.</p>
          <div className="mt-6 space-y-3">
            {["Frame the problem", "Surface assumptions", "Reason one step", "Test the logic", "Reach the insight"].map((step, i) => {
              const active = i < assistantTurns;
              const current = i === assistantTurns - 1;
              return (
                <div key={step} data-testid={`logic-node-${i}`} className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                    current ? "bg-persian-blue text-navy ring-2 ring-persian-blue/40"
                      : active ? "bg-persian-blue/20 text-persian-blue" : "bg-white/5 text-slate-500"}`}>
                    <CircleDot className="h-4 w-4" />
                  </div>
                  <div className={`flex-1 rounded-lg px-3 py-2 text-sm transition-all ${
                    active ? "bg-white/10 text-white" : "bg-white/[0.03] text-slate-500"}`}>{step}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-4">
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">Method note</span>
            <p className="text-sm text-slate-300 mt-2 leading-relaxed">
              The tutor withholds final answers by design. It escalates hints only after genuine effort—training durable reasoning, not recall.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <p className="text-slate-400 text-sm mb-3">Sketch equations or graphs, then send them for the tutor to review.</p>
          <Whiteboard onAttach={attachSketch} />
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-6">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">Guided reasoning</span>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-2">Socratic AI Tutor</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Never handed the answer—guided step by step through economics and math logic loops. Choose a discipline, then reason together.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {SUBJECTS.map((s) => (
          <button key={s.id} onClick={() => setSubject(s.id)} data-testid={`subject-${s.id.toLowerCase()}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              subject === s.id ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-persian-blue"}`}>
            <s.icon className="h-4 w-4" /> {s.id}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* History sidebar (collapsible) */}
        {sidebarOpen ? (
          <aside className="lg:w-72 shrink-0 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-3 flex flex-col"
            style={{ maxHeight: "640px" }} data-testid="chat-history-panel">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400 font-semibold">History</span>
              <button onClick={() => setSidebarOpen(false)} data-testid="sidebar-collapse-button"
                title="Collapse sidebar" className="text-slate-400 hover:text-navy transition-colors p-1 rounded-md hover:bg-navy-soft">
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={startNewChat} data-testid="new-chat-button"
              className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold mb-3">
              <Plus className="h-4 w-4 mr-1.5" /> New Chat
            </Button>
            <div className="overflow-y-auto space-y-1 flex-1">
              {sessions.length === 0 && (
                <p className="text-xs text-slate-400 px-2 py-3 text-center">Your past conversations will appear here.</p>
              )}
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => openSession(s)}
                  data-testid={`chat-session-${s.session_id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                    s.session_id === sessionId ? "bg-navy text-white" : "text-slate-600 hover:bg-navy-soft"}`}>
                  <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </aside>
        ) : (
          <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-expand-button"
            title="Show history"
            className="self-start flex items-center gap-2 bg-white rounded-xl border border-slate-200/80 shadow-sm px-3 py-2.5 text-sm font-medium text-navy hover:border-persian-blue transition-colors">
            <PanelLeftOpen className="h-4 w-4" /> <span className="lg:hidden xl:inline">History</span>
          </button>
        )}

        {/* Workspace: chat + right pane (single render to keep test ids unique) */}
        <div className="flex-1 min-w-0">
          {isDesktop ? (
            <div style={{ height: "640px" }}>
              <ResizablePanelGroup direction="horizontal" className="rounded-2xl">
                <ResizablePanel defaultSize={58} minSize={35}>
                  <div className="h-full pr-3">{ChatPane}</div>
                </ResizablePanel>
                <ResizableHandle withHandle data-testid="tutor-resize-handle" />
                <ResizablePanel defaultSize={42} minSize={25}>
                  <div className="h-full pl-3">{RightPane}</div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          ) : (
            <div className="space-y-6">
              <div style={{ height: "520px" }}>{ChatPane}</div>
              <div style={{ minHeight: "560px" }}>{RightPane}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
